import { useQuery } from '@tanstack/react-query';
import { getTaskOverview, taskOverviewKey } from '../api';
import { ProjectProgressLine } from './ProjectProgressLine';

/**
 * Where the work is, on a screen that has none of yours.
 *
 * A reporter invited to review somebody else's project holds no tasks, and every landing surface
 * is built on `mine` — which is the right default and stays the default. So the product he was
 * invited to opened on an empty timeline, "Nothing waiting", "Nothing here. Capture something
 * above." and a bell reading 0, with the project he was invited *to* three non-obvious clicks
 * away behind the Tasks scope dropdown. The board at the end of those clicks answers his actual
 * question — who is stuck, who has finished — genuinely well. The screen existed; nothing pointed
 * at it. This is the pointer, and it lands on the board rather than the project's own panel
 * because the board is the screen that was worth finding.
 *
 * Renders nothing at all unless the server says there is somewhere to send you: `elsewhere` is
 * empty for anyone holding open work of their own and for anyone whose projects are their own
 * (see `TaskOverview.elsewhere`), so the screens below are unchanged for every account that is
 * not in this situation. The condition is the server's rather than "the list I am under is
 * empty", because an empty *filtered* list is not the same claim as "nothing is assigned to you",
 * and this says the second one.
 *
 * A band in the same shape as `CarriedOver` rather than a third grey sentence under two grey
 * sentences: the whole finding is that the way on was invisible, and the first draft of this —
 * inside the shortlist column, which is a 160px rail — wrapped its own heading onto three lines.
 *
 * The counts come from `ProjectProgressLine`, the projects screen's own line, so the pointer and
 * the project it points at cannot end up describing that project differently.
 */
export function WorkElsewhere({ onOpenBoard }: { onOpenBoard: (projectId: string) => void }) {
  /* `mine`, which is the whole premise — "nothing is assigned to you" is a claim about your own
     work, and `elsewhere` is computed over `myWork` whatever is asked for. Shares the key, and so
     the fetch, with the late half of `CarriedOver`. */
  const overview = useQuery({
    queryKey: taskOverviewKey(),
    queryFn: () => getTaskOverview(),
  });
  const projects = overview.data?.elsewhere ?? [];
  /* Silent on failure and while loading: this is a hint beside an empty list, and an error about
     a hint would be louder than the thing it is hinting at. */
  if (projects.length === 0) return null;

  return (
    <div className="work-elsewhere" data-testid="work-elsewhere">
      <p className="elsewhere-head">
        <strong>Nothing is assigned to you.</strong>{' '}
        <span className="subtext">
          There is work in {projects.length === 1 ? 'a project' : 'projects'} you are part of. Open
          the board to see who is stuck, who has finished, and what is unclaimed.
        </span>
      </p>
      <ul className="elsewhere-list">
        {projects.map((p) => (
          <li key={p.projectId}>
            <button
              className="link"
              data-testid="elsewhere-project"
              data-project-id={p.projectId}
              onClick={() => onOpenBoard(p.projectId)}
            >
              {p.title}
            </button>
            <ProjectProgressLine progress={p.progress} />
          </li>
        ))}
      </ul>
    </div>
  );
}
